import React, { useState, useEffect } from 'react';
import { X, Search, Package2, Plus, Check } from 'lucide-react';
import { StockItem } from '../lib/stockItems';
import { supabase } from '../lib/supabase';
import SelectSellableItemsModal from './SelectSellableItemsModal';

interface SellableItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  businessId?: string;
}

export default function SellableItemsModal({
  isOpen,
  onClose,
  onUpdate,
  businessId
}: SellableItemsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);

  const loadSellableItems = async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('stock_items')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_sellable', true)
        .order('name');

      if (error) throw error;
      setItems(data);
      setError(null);
    } catch (err) {
      console.error('Error loading sellable items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sellable items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadSellableItems();
    }
  }, [isOpen, businessId]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
    }
  };

  const handleRemoveFromSellable = async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('stock_items')
        .update({ is_sellable: false })
        .eq('id', itemId);

      if (error) throw error;
      await loadSellableItems();
      onUpdate();
    } catch (err) {
      console.error('Error removing sellable item:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove item');
    }
  };

  const filteredItems = items.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-gray-900/50 z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      >
        <div 
          className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-xl transform transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onTransitionEnd={handleTransitionEnd}
          onClick={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Sellable Items</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search sellable items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setIsSelectModalOpen(true)}
                className="ml-4 inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Items
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading items...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-6">
                  <div className="text-red-600 mb-2">{error}</div>
                  <button
                    onClick={loadSellableItems}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Try again
                  </button>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-6">
                  <Package2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">
                    {searchQuery ? 'No matching items' : 'No sellable items'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {searchQuery ? 'Try adjusting your search terms' : 'Add items to make them available for sale'}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => setIsSelectModalOpen(true)}
                      className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Sellable Items
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredItems.map((item) => (
                    <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                          {item.description && (
                            <p className="mt-1 text-sm text-gray-500">{item.description}</p>
                          )}
                          <div className="mt-2 text-xs text-gray-500">
                            ${item.price.toFixed(2)} per {item.unit}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveFromSellable(item.id)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SelectSellableItemsModal
        isOpen={isSelectModalOpen}
        onClose={() => setIsSelectModalOpen(false)}
        onSuccess={() => {
          setIsSelectModalOpen(false);
          loadSellableItems();
        }}
        businessId={businessId}
      />
    </>
  );
}